import * as React from "react";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down.js";
import LogOut from "lucide-react/dist/esm/icons/log-out.js";
import Mic from "lucide-react/dist/esm/icons/mic.js";
import { Avatar, AvatarFallback } from "@shared/ui/avatar";
import { Button } from "@shared/ui/button";
import { activeLanguagePrefix } from "@shared/lib/localized-path";
import { cn } from "@shared/lib/utils";
import { usePaymentConfig } from "@shared/hooks/use-paywall";
import {
  buildNavGroups,
  buildStandaloneNavItems,
  type NavItem,
  type SystemUser,
} from "@shared/layouts/system-shell";

const SUPPORTED_LANGUAGES = ["en", "ar", "de", "es", "fr"] as const;

function stripLanguagePrefix(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (SUPPORTED_LANGUAGES.includes(segments[0] as typeof SUPPORTED_LANGUAGES[number])) {
    const stripped = `/${segments.slice(1).join("/")}`;
    return stripped === "/" ? "/" : stripped.replace(/\/$/, "");
  }
  return pathname === "" ? "/" : pathname;
}

function localizeHref(href: string, pathname: string): string {
  const prefix = activeLanguagePrefix(pathname);
  if (!prefix || href === "/" || href === prefix || href.startsWith(`${prefix}/`)) {
    return href;
  }
  return `${prefix}${href}`;
}

function isActiveNavItem(activePath: string, href: string): boolean {
  return activePath === href || (href !== "/" && activePath.startsWith(`${href}/`));
}

function getActiveNavItem(
  items: readonly NavItem[],
  activePath: string,
): NavItem | null {
  let activeItem: NavItem | null = null;

  for (const item of items) {
    if (
      isActiveNavItem(activePath, item.href) &&
      (!activeItem || item.href.length > activeItem.href.length)
    ) {
      activeItem = item;
    }
  }

  return activeItem;
}

function getUserInitials(user?: SystemUser | null): string {
  const source = user?.name || user?.email || "User";
  return source
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

function translateRouteLabel(
  t: TFunction<"common">,
  item: NavItem,
): string {
  const key = `navigation.routes.${item.name.replace(/-i18n$/, "")}`;
  const translated = t(key, { defaultValue: item.label });
  return translated === key ? item.label : translated;
}

function translateGroupLabel(
  t: TFunction<"common">,
  groupKey: string,
  fallback: string,
): string {
  const key = `navigation.groups.${groupKey}`;
  const translated = t(key, { defaultValue: fallback });
  return translated === key ? fallback : translated;
}

const VOICE_LOG_HREF = "/health-assistant/voice-log";

export interface MobileMenuNavProps {
  items: readonly NavItem[];
  pathname: string;
  user?: SystemUser | null;
  onLogout?: () => void;
}

export function MobileMenuNav({
  items,
  pathname,
  user,
  onLogout,
}: MobileMenuNavProps) {
  const { t } = useTranslation("common");
  const activePath = stripLanguagePrefix(pathname);
  const activeItem = getActiveNavItem(items, activePath);
  // A parent of the current leaf (e.g. Health Assistant on the voice-log page):
  // "true", never "page" — that belongs to the leaf alone.
  const isAncestorOfActive = (href: string): boolean =>
    activeItem?.href !== href && isActiveNavItem(activePath, href);
  // Voice Log must stay reachable from the hamburger whenever the deployment
  // offers it, even if the route table did not surface it as a nav item. Same
  // availability rule as the quick-add sheet.
  const { data: paymentConfig } = usePaymentConfig();
  const voiceAvailable = Boolean(
    (paymentConfig?.paymentsEnabled || paymentConfig?.byokEnabled) &&
      paymentConfig?.aiFeaturesEnabled?.health_assistant_voice,
  );
  const groupedItems = React.useMemo(() => buildNavGroups(items), [items]);
  const standaloneItems = React.useMemo(
    () => buildStandaloneNavItems(items),
    [items],
  );
  const [openGroupKeys, setOpenGroupKeys] = React.useState<string[]>(() =>
    activeItem?.groupKey ? [activeItem.groupKey] : [],
  );

  React.useEffect(() => {
    if (activeItem?.groupKey) {
      setOpenGroupKeys([activeItem.groupKey]);
    }
  }, [activeItem?.groupKey]);

  const setGroupOpen = React.useCallback((groupKey: string, open: boolean) => {
    setOpenGroupKeys(open ? [groupKey] : []);
  }, []);

  const showVoiceFallback =
    voiceAvailable && !items.some((item) => item.href === VOICE_LOG_HREF);
  const voiceActive = isActiveNavItem(activePath, VOICE_LOG_HREF);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {user && (
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs font-semibold">
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {user.name || t("actions.user.fallbackName", "User")}
            </p>
            {user.email && (
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
        </div>
      )}
      <nav
        aria-label={t("navigation.dashboardNavigation", "Dashboard navigation")}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-4"
      >
        <div className="space-y-2">
          {(standaloneItems.length > 0 || showVoiceFallback) && (
            <div className="mb-3 space-y-1 border-b border-border pb-3">
              {standaloneItems.map((item) => {
                const Icon = item.icon;
                const localizedHref = localizeHref(item.href, pathname);
                const active = activeItem?.href === item.href;
                const isParent = !active && isAncestorOfActive(item.href);
                const itemLabel = translateRouteLabel(t, item);
                return (
                  <Link
                    key={item.href}
                    to={localizedHref as never}
                    aria-current={active ? "page" : isParent ? "true" : undefined}
                    className={cn(
                      "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      active
                        ? "border-s-2 border-primary bg-primary/15 text-foreground"
                        : isParent
                          ? "border-s-2 border-dashed border-primary/60 text-foreground hover:bg-accent hover:text-accent-foreground"
                          : "text-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{itemLabel}</span>
                  </Link>
                );
              })}
              {showVoiceFallback && (
                <Link
                  to={localizeHref(VOICE_LOG_HREF, pathname) as never}
                  data-testid="mobile-menu-voice-log"
                  aria-current={voiceActive ? "page" : undefined}
                  className={cn(
                    "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    voiceActive
                      ? "border-s-2 border-primary bg-primary/15 text-foreground"
                      : "text-foreground hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <Mic className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">
                    {t(
                      "navigation.routes.health-assistant-voice-log",
                      "Voice Log",
                    )}
                  </span>
                </Link>
              )}
            </div>
          )}
          {groupedItems.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = openGroupKeys.includes(group.key);
            const hasActiveItem = activeItem?.groupKey === group.key;
            const groupLabel = translateGroupLabel(t, group.key, group.label);
            return (
              <div key={group.key}>
                <button
                  type="button"
                  className={cn(
                    "flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors",
                    hasActiveItem
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  )}
                  aria-expanded={isOpen}
                  onClick={() => setGroupOpen(group.key, !isOpen)}
                >
                  <GroupIcon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-start">
                    {groupLabel}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 transition-transform",
                      isOpen && "rotate-180",
                    )}
                    aria-hidden="true"
                  />
                </button>
                {isOpen && (
                  <div className="mt-1 space-y-1">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const localizedHref = localizeHref(item.href, pathname);
                      const active = activeItem?.href === item.href;
                      const isParent = !active && isAncestorOfActive(item.href);
                      const itemLabel = translateRouteLabel(t, item);
                      return (
                        <Link
                          key={item.href}
                          to={localizedHref as never}
                          aria-current={active ? "page" : isParent ? "true" : undefined}
                          className={cn(
                            "flex min-h-11 items-center gap-3 rounded-md px-3 ps-10 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            active
                              ? "bg-primary/20 text-foreground"
                              : isParent
                                ? "border-s-2 border-dashed border-primary/60 text-foreground hover:bg-accent hover:text-accent-foreground"
                                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{itemLabel}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
      {onLogout && (
        <div className="border-t border-border p-3">
          <Button
            type="button"
            variant="ghost"
            className="min-h-11 w-full justify-start gap-3 text-destructive hover:text-destructive"
            onClick={onLogout}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t("navigation.logout", "Log out")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default MobileMenuNav;
